import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { RobotDevice } from '../../types';
import {
  createRobotFaceEmbeddings,
  createRobotIdentity,
  deleteRobotFaceEmbeddings,
  deleteRobotIdentity,
  updateRobotFaceEmbeddings,
  updateRobotIdentity,
} from '../api';
import { RobotIdentity } from '../types';
import { robotIdentityKeys } from './keys';

export const useCreateRobotIdentity = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<
    RobotIdentity,
    unknown,
    Partial<RobotIdentity>,
    unknown
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: async (newIdentity) => {
      const res = await createRobotIdentity(
        robot?.websocketConnection?.ipAddress,
        newIdentity
      );

      if (!res?.uuid) {
        throw Error('Creating identity failed with an unknown error');
      }

      return res;
    },
    onSettled: (_data, error, _variables, context) => {
      // Invalidate all queries that may contain stale data due to this identity update
      queryClient.invalidateQueries(robotIdentityKeys.identities(robotId));
    },
    ...options,
  });

  return mutation;
};

export const useUpdateRobotIdentity = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<
    RobotIdentity,
    unknown,
    { identity_id: string; identityUpdates: Partial<RobotIdentity> },
    { previousValue: unknown }
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: async ({ identity_id, identityUpdates }) => {
      const res = await updateRobotIdentity(
        robot?.websocketConnection?.ipAddress,
        identity_id,
        identityUpdates
      );

      if (!res?.uuid) {
        throw Error('Updating identity failed with an unknown error');
      }

      return res;
    },
    onMutate: async ({ identity_id, identityUpdates }) => {
      const queryKey = robotIdentityKeys.identity(robotId, identity_id);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous value
      const previousValue = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old: RobotIdentity | undefined) => ({
        ...(old ?? { uuid: identity_id }),
        ...identityUpdates,
      }));

      // Return a context with the previous value
      return { previousValue };
    },
    onSettled: (_data, error, { identity_id }, context) => {
      if (error) {
        queryClient.setQueryData(
          robotIdentityKeys.identity(robotId, identity_id),
          context?.previousValue
        );
      }

      // Invalidate all queries that may contain stale data due to this identity update
      queryClient.invalidateQueries(robotIdentityKeys.identities(robotId));
    },
    ...options,
  });

  return mutation;
};

export const useDeleteRobotIdentity = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<
    void,
    unknown,
    string,
    { previousValue: unknown }
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: (identity_id) =>
      deleteRobotIdentity(robot?.websocketConnection?.ipAddress, identity_id),
    onSettled: () => {
      // Invalidate all queries that may contain stale data due to this identity update
      queryClient.invalidateQueries(robotIdentityKeys.identities(robotId));
    },
    ...options,
  });

  return mutation;
};

export const useCreateRobotFaceEmbeddings = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<RobotIdentity, unknown, string, unknown>
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: (name: string) =>
      createRobotFaceEmbeddings(robot?.websocketConnection?.ipAddress, name),
    onSuccess: (newIdentity) => {
      queryClient.setQueryData(
        robotIdentityKeys.identity(robotId, newIdentity.uuid),
        newIdentity
      );
    },
    onSettled: () => {
      // Invalidate all queries that may contain stale data due to this identity update
      queryClient.invalidateQueries(robotIdentityKeys.identities(robotId));
    },
    ...options,
  });

  return mutation;
};

export const useUpdateRobotFaceEmbeddings = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<void, unknown, string, unknown>
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: (identity_id: string) =>
      updateRobotFaceEmbeddings(
        robot?.websocketConnection?.ipAddress,
        identity_id ?? ''
      ),
    onSuccess: (_data, identity_id) => {
      queryClient.setQueryData(
        robotIdentityKeys.identity(robotId, identity_id),
        (old: RobotIdentity | undefined) => {
          return {
            ...(old ?? { uuid: identity_id }),
            has_face_embeddings: true,
          };
        }
      );
    },
    onSettled: () => {
      // Invalidate all queries that may contain stale data due to this identity update
      queryClient.invalidateQueries(robotIdentityKeys.identities(robotId));
    },
    ...options,
  });

  return mutation;
};

export const useDeleteRobotFaceEmbeddings = (
  robot: RobotDevice | undefined,
  options?: UseMutationOptions<void, unknown, string, unknown>
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const mutation = useMutation({
    mutationFn: (identity_id) =>
      deleteRobotFaceEmbeddings(
        robot?.websocketConnection?.ipAddress,
        identity_id
      ),
    onSuccess: (_data, identity_id) => {
      queryClient.setQueryData(
        robotIdentityKeys.identity(robotId, identity_id),
        (old: RobotIdentity | undefined) => {
          return {
            ...(old ?? { uuid: identity_id }),
            has_face_embeddings: true,
          };
        }
      );
    },
    onSettled: () => {
      // Invalidate all queries that may contain stale data due to this identity update
      queryClient.invalidateQueries(robotIdentityKeys.identities(robotId));
    },
    ...options,
  });

  return mutation;
};
